/**
 * FileSharePanel — 通用文件管理面板
 *
 * 用法 (嵌入任何实体详情页的 "Files" 标签页):
 *   <FileSharePanel entityType="job" entityId={jobId} />
 *
 * 功能:
 *   - 查看该实体关联的所有文件 (拉取 /fileshare/entity/:type/:id/files)
 *   - 拖放 / 点击上传文件
 *   - 下载 / 预览文件
 *   - 收藏书签
 *   - 添加标签 (选择已有或创建新标签)
 *   - 添加笔记
 *   - 按文件名 / docType 搜索
 *   - 归档文件
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bookmark,
  BookmarkCheck,
  Check,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  FileArchive,
  FileImage,
  FileText,
  FileVideo,
  Link2,
  Loader2,
  Lock,
  Mail,
  MessageCircle,
  MessageSquare,
  Plus,
  Search,
  Share2,
  Tag,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { declareFilePurpose, uploadFileToFileShare, type FileUploadContext } from '@/lib/file-intake-client';
import type { FileIntakeActionMode, FileIntakePurpose } from '@/lib/file-intake-dsl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FilePurposeDialog, type FilePurposeDialogValue } from '@/components/files/FilePurposeDialog';

// ─── Types ────────────────────────────────────────────────────────

interface FileTag {
  id: string;
  name: string;
  color: string | null;
}

interface FileNote {
  id: string;
  content: string;
  author_name: string | null;
  is_pinned: boolean;
  created_at: string;
}

interface FileEntityLink {
  id: string;
  file_id: string;
  entity_type: string;
  entity_id: string;
  entity_ref?: string | null;
  link_type?: string | null;
  linked_by?: string | null;
  created_at?: string;
}

interface FileRecord {
  id: string;
  file_name: string | null;
  display_name: string | null;
  description: string | null;
  file_url: string;
  mime_type: string | null;
  file_size: number | null;
  doc_type: string | null;
  ai_doc_type: string | null;
  ai_summary: string | null;
  parse_status?: string | null;
  rag_status?: string | null;
  metadata?: Record<string, unknown> | null;
  source: string;
  source_channel: string | null;
  is_archived: boolean;
  created_at: string;
  tags?: FileTag[];
  isBookmarked?: boolean;
  notes?: FileNote[];
  entityLinks?: FileEntityLink[];
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface ShareLink {
  id: string;
  token: string;
  recipient_name: string | null;
  recipient_email: string | null;
  note: string | null;
  expires_at: string | null;
  is_active: boolean;
  accessed_at: string | null;
  access_count: number;
  created_at: string;
  shareUrl: string;
  passwordProtected?: boolean;
}

interface CreatedShareEntry {
  url: string;
  fileName: string;
  shareId: string;
  plainPassword?: string | null;
  recipientEmail?: string | null;
}

// ─── Props ────────────────────────────────────────────────────────

interface FileSharePanelProps {
  entityType: string;
  entityId: string;
  readOnly?: boolean;
  onFilesLoaded?: (files: FileRecord[], total: number) => void;
  intakeContext?: {
    enablePurposeDeclaration?: boolean;
    sourceSurface?: string;
    sourceChannel?: 'web_upload' | 'go_pwa' | 'api' | 'system';
    defaultPurpose?: FileIntakePurpose;
    defaultActionMode?: FileIntakeActionMode;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}

function FileIcon({ mime, className = 'w-8 h-8' }: { mime: string | null; className?: string }) {
  if (!mime) return <FileText className={className} />;
  if (mime.startsWith('image/')) return <FileImage className={className} />;
  if (mime.startsWith('video/')) return <FileVideo className={className} />;
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('tar')) return <FileArchive className={className} />;
  return <FileText className={className} />;
}

function tagColor(color: string | null): string {
  const map: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700',
    red: 'bg-red-100 text-red-700',
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    purple: 'bg-purple-100 text-purple-700',
    orange: 'bg-orange-100 text-orange-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  return map[color ?? ''] ?? 'bg-slate-100 text-slate-600';
}

// ─── Component ────────────────────────────────────────────────────

export default function FileSharePanel({ entityType, entityId, readOnly = false, onFilesLoaded, intakeContext }: FileSharePanelProps) {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [search, setSearch] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [dragging, setDragging] = useState(false);

  // Tag management
  const [allTags, setAllTags] = useState<FileTag[]>([]);
  const [tagDialogFileId, setTagDialogFileId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');

  // Note management
  const [noteDialogFileId, setNoteDialogFileId] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileRecord[]>([]);
  const LIMIT = 20;

  // ── Batch selection ─────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(files.map((f) => f.id)));
  const allSelected = files.length > 0 && files.every((f) => selected.has(f.id));

  // ── Share dialog state ─────────────────────────────────────────
  const [shareDialogFileIds, setShareDialogFileIds] = useState<string[]>([]);
  const shareDialogOpen = shareDialogFileIds.length > 0;
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
const [shareForm, setShareForm] = useState({ recipientName: '', recipientEmail: '', note: '', expiresInDays: '', generatePassword: false });
  const [shareCreating, setShareCreating] = useState(false);
  const [newShareUrls, setNewShareUrls] = useState<CreatedShareEntry[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<Set<string>>(new Set());

  // ── Load files ─────────────────────────────────────────────────
  const loadFiles = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(LIMIT),
        include: 'tags,bookmark,links',
      });
      if (search) params.set('search', search);
      if (filterDocType) params.set('docType', filterDocType);

      const res = await api.get<PaginatedResponse<FileRecord>>(
        `/fileshare/entity/${entityType}/${entityId}/files?${params}`,
      );
      const nextFiles = p === 1 ? res.items : [...filesRef.current, ...res.items];
      filesRef.current = nextFiles;
      setFiles(nextFiles);
      setTotal(res.total);
      setPage(p);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, search, filterDocType]);

  useEffect(() => {
    void loadFiles(1);
  }, [loadFiles]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    onFilesLoaded?.(files, total);
  }, [files, total, onFilesLoaded]);

  // ── Load all tags ───────────────────────────────────────────────
  useEffect(() => {
    api.get<FileTag[]>('/fileshare/tags').then(setAllTags).catch(() => {});
  }, []);

  // ── Upload ──────────────────────────────────────────────────────
  const handleUpload = useCallback(async (fileList: FileList | File[] | null, declaration?: FilePurposeDialogValue) => {
    if (!fileList || readOnly) return;
    setUploading(true);
    try {
      const filesToUpload = Array.isArray(fileList) ? fileList : Array.from(fileList);
      const uploadContext: FileUploadContext = {
        entityType,
        entityId,
        source: 'upload',
        sourceChannel: intakeContext?.sourceChannel ?? 'web_upload',
        autoAnalyze: Boolean(declaration),
        metadata: declaration ? {
          sourceSurface: intakeContext?.sourceSurface ?? 'fileshare',
          uploadPurpose: declaration.purpose,
          uploadInstruction: declaration.instruction || null,
          uploadSite: declaration.site || null,
          uploadActionMode: declaration.autoActionMode,
        } : undefined,
      };

      for (const file of filesToUpload) {
        const uploaded = await uploadFileToFileShare(file, uploadContext);
        if (declaration) {
          await declareFilePurpose(uploaded.fileId, {
            purpose: declaration.purpose,
            instruction: declaration.instruction || null,
            site: declaration.site || null,
            autoActionMode: declaration.autoActionMode,
            autoIndexKnowledge: declaration.autoIndexKnowledge,
            sourceSurface: intakeContext?.sourceSurface ?? 'fileshare',
            sourceChannel: intakeContext?.sourceChannel ?? 'web_upload',
            targetEntityType: entityType,
            targetEntityId: entityId,
          });
        }
      }
      await loadFiles(1);
      if (intakeContext?.enablePurposeDeclaration || intakeContext?.sourceSurface?.startsWith('operational-intelligence')) {
        window.dispatchEvent(new Event('operational-intelligence:refresh'));
      }
    } catch {
      // silent
    } finally {
      setUploading(false);
      setPendingUploadFiles([]);
    }
  }, [entityType, entityId, intakeContext?.sourceChannel, intakeContext?.sourceSurface, loadFiles, readOnly]);

  // ── Drag-and-drop ───────────────────────────────────────────────
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (readOnly) return;
    void handleUpload(e.dataTransfer.files);
  }, [handleUpload, readOnly]);

  // ── Bookmark toggle ─────────────────────────────────────────────
  const toggleBookmark = async (file: FileRecord) => {
    if (file.isBookmarked) {
      await api.delete(`/fileshare/files/${file.id}/bookmark`);
    } else {
      await api.post(`/fileshare/files/${file.id}/bookmark`, {});
    }
    setFiles((prev) => prev.map((f) => f.id === file.id ? { ...f, isBookmarked: !f.isBookmarked } : f));
  };

  // ── Archive ──────────────────────────────────────────────────────
  const archiveFile = async (fileId: string) => {
    await api.post(`/fileshare/files/${fileId}/archive`, {});
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    setTotal((t) => t - 1);
  };

  // ── Tag dialog ──────────────────────────────────────────────────
  const addTag = async (fileId: string, tagName: string) => {
    if (!tagName.trim()) return;
    await api.post(`/fileshare/files/${fileId}/tags`, { tags: [tagName.trim()] });
    // Refresh full tags for this file
    const updated = await api.get<FileRecord>(`/fileshare/files/${fileId}`);
    setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, tags: (updated as any).tags ?? [] } : f));
    await api.get<FileTag[]>('/fileshare/tags').then(setAllTags).catch(() => {});
  };

  const removeTag = async (fileId: string, tagId: string) => {
    await api.delete(`/fileshare/files/${fileId}/tags/${tagId}`);
    setFiles((prev) => prev.map((f) => f.id === fileId
      ? { ...f, tags: (f.tags ?? []).filter((t) => t.id !== tagId) }
      : f));
  };

  // ── Note dialog ─────────────────────────────────────────────────
  const submitNote = async () => {
    if (!noteDialogFileId || !noteInput.trim()) return;
    setNoteSubmitting(true);
    try {
      await api.post(`/fileshare/files/${noteDialogFileId}/notes`, { content: noteInput.trim() });
      setNoteInput('');
      setNoteDialogFileId(null);
    } finally {
      setNoteSubmitting(false);
    }
  };

  // ── Open share dialog ───────────────────────────────────────────
  const openShareDialog = useCallback(async (fileIds: string[]) => {
    setShareDialogFileIds(fileIds);
    setNewShareUrls([]);
    setEmailSent(new Set());
    setShareForm({ recipientName: '', recipientEmail: '', note: '', expiresInDays: '', generatePassword: false });
    setCopied(null);
    // Load existing shares for single-file dialog
    if (fileIds.length === 1) {
      try {
        const res = await api.get<ShareLink[]>(`/fileshare/files/${fileIds[0]}/shares`);
        setShareLinks(Array.isArray(res) ? res : []);
      } catch {
        setShareLinks([]);
      }
    } else {
      setShareLinks([]);
    }
  }, []);

  const closeShareDialog = useCallback(() => {
    setShareDialogFileIds([]);
    setNewShareUrls([]);
    setShareLinks([]);
    setCopied(null);
    setEmailSent(new Set());
  }, []);

  const createShareLinks = useCallback(async () => {
    setShareCreating(true);
    try {
      const created: CreatedShareEntry[] = [];
      for (const fileId of shareDialogFileIds) {
        const file = files.find((f) => f.id === fileId);
        const res = await api.post<ShareLink & { shareUrl: string; plainPassword?: string }>(`/fileshare/files/${fileId}/share`, {
          recipientName:    shareForm.recipientName || null,
          recipientEmail:   shareForm.recipientEmail || null,
          note:             shareForm.note || null,
          expiresInDays:    shareForm.expiresInDays ? Number(shareForm.expiresInDays) : null,
          generatePassword: shareForm.generatePassword || undefined,
        });
        created.push({
          url: res.shareUrl,
          shareId: res.id,
          fileName: file?.display_name ?? file?.file_name ?? 'File',
          plainPassword: res.plainPassword ?? null,
          recipientEmail: shareForm.recipientEmail || null,
        });
      }
      setNewShareUrls(created);
      // Auto-copy first URL if single file
      if (created.length === 1 && created[0]) {
        await navigator.clipboard.writeText(created[0].url).catch(() => {});
        setCopied(created[0].url);
      }
      // Refresh share history
      if (shareDialogFileIds.length === 1) {
        const updated = await api.get<ShareLink[]>(`/fileshare/files/${shareDialogFileIds[0]}/shares`);
        setShareLinks(Array.isArray(updated) ? updated : []);
      }
    } catch {
      // silent — could add toast
    } finally {
      setShareCreating(false);
    }
  }, [shareDialogFileIds, shareForm, files]);

  const revokeShareLink = useCallback(async (shareId: string) => {
    await api.delete(`/fileshare/shares/${shareId}`);
    setShareLinks((prev) => prev.map((l) => l.id === shareId ? { ...l, is_active: false } : l));
  }, []);

  const copyUrl = useCallback(async (url: string) => {
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopied(url);
    setTimeout(() => setCopied((c) => c === url ? null : c), 2000);
  }, []);

  const sendShareEmail = useCallback(async (shareId: string, recipientEmail: string, plainPassword?: string | null) => {
    setEmailSending(shareId);
    try {
      await api.post(`/fileshare/shares/${shareId}/send-email`, {
        recipientEmail,
        ...(plainPassword ? { plainPassword } : {}),
      });
      setEmailSent((prev) => new Set([...prev, shareId]));
    } catch {
      // silent — user can retry
    } finally {
      setEmailSending(null);
    }
  }, []);

  const shareViaWhatsApp = useCallback((url: string, fileName: string, isPasswordProtected: boolean) => {
    const msg = isPasswordProtected
      ? `I've shared a file with you: "${fileName}"

🔒 This file is password protected. I will send you the password separately.

${url}`
      : `I've shared a file with you: "${fileName}"

${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  }, []);

  // ── Bulk archive ──────────────────────────────────────────────────
  const bulkArchive = useCallback(async () => {
    for (const id of selected) {
      await archiveFile(id);
    }
    clearSelection();
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Unique doc types for filter dropdown ────────────────────────
  const docTypes = [...new Set(files.map((f) => f.doc_type ?? f.ai_doc_type).filter(Boolean) as string[])];

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-3">

      {/* ─ Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>

        {docTypes.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                {filterDocType || 'All types'} <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterDocType('')}>All types</DropdownMenuItem>
              <DropdownMenuSeparator />
              {docTypes.map((dt) => (
                <DropdownMenuItem key={dt} onClick={() => setFilterDocType(dt)}>
                  {dt}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!readOnly && (
          <>
            <Button
              size="sm"
              className="h-8 text-xs gap-1"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const nextFiles = Array.from(e.target.files ?? []);
                if (!nextFiles.length) return;
                if (intakeContext?.enablePurposeDeclaration) {
                  setPendingUploadFiles(nextFiles);
                } else {
                  void handleUpload(nextFiles);
                }
                e.currentTarget.value = '';
              }}
            />
          </>
        )}
      </div>

      {/* ─ Stat bar ─────────────────────────────────────────────── */}
      {total > 0 && (
        <div className="text-xs text-muted-foreground">
          {total} file{total !== 1 ? 's' : ''}
        </div>
      )}

      {/* ─ Drop zone ────────────────────────────────────────────── */}
      {!readOnly && (
        <div
          className={`rounded-lg border-2 border-dashed transition-colors py-4 text-center text-xs text-muted-foreground cursor-pointer
            ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/40'}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading
            ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</span>
            : <span><Plus className="w-3.5 h-3.5 inline mr-1" />Drop files here or click to upload</span>
          }
        </div>
      )}

      {/* ─ Loading ──────────────────────────────────────────────── */}
      {loading && files.length === 0 && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* ─ Empty state ──────────────────────────────────────────── */}
      {!loading && files.length === 0 && (
        <div className="text-center py-10 text-muted-foreground text-xs">
          No files yet. Upload one to get started.
        </div>
      )}

      {/* ─ Select-all strip ─────────────────────────────────────── */}
      {files.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <button
            onClick={allSelected ? clearSelection : selectAll}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center transition-colors
              ${allSelected ? 'bg-primary border-primary' : 'border-border'}`}>
              {allSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
            </span>
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
          {selected.size > 0 && !allSelected && (
            <span className="text-xs text-muted-foreground">— {selected.size} selected</span>
          )}
        </div>
      )}

      {/* ─ File list ────────────────────────────────────────────── */}
      <div className="flex flex-col divide-y divide-border rounded-md border overflow-hidden">
        {files.map((file) => (
          <FileRow
            key={file.id}
            file={file}
            readOnly={readOnly}
            isSelected={selected.has(file.id)}
            onSelectToggle={() => toggleSelect(file.id)}
            allTags={allTags}
            tagInput={tagInput}
            tagDialogOpen={tagDialogFileId === file.id}
            onOpenTagDialog={() => { setTagDialogFileId(file.id); setTagInput(''); }}
            onCloseTagDialog={() => setTagDialogFileId(null)}
            onTagInputChange={setTagInput}
            onAddTag={(name) => { void addTag(file.id, name); setTagInput(''); }}
            onRemoveTag={(tagId) => void removeTag(file.id, tagId)}
            onToggleBookmark={() => void toggleBookmark(file)}
            onOpenNoteDialog={() => setNoteDialogFileId(file.id)}
            onArchive={() => void archiveFile(file.id)}
            onShare={() => void openShareDialog([file.id])}
          />
        ))}
      </div>

      {/* ─ Load more ────────────────────────────────────────────── */}
      {files.length < total && (
        <Button
          variant="ghost"
          size="sm"
          className="text-xs self-center"
          onClick={() => void loadFiles(page + 1)}
          disabled={loading}
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          Load more ({total - files.length} remaining)
        </Button>
      )}

      {/* ─ Bulk action bar (floats when items are selected) ──────── */}
      {selected.size > 0 && (
        <div className="sticky bottom-4 z-20 mx-auto flex items-center gap-2 rounded-full border bg-background px-4 py-2 shadow-lg shadow-black/10">
          <span className="text-xs font-medium">{selected.size} selected</span>
          <div className="h-3.5 w-px bg-border" />
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 rounded-full"
            onClick={() => void openShareDialog([...selected])}
          >
            <Share2 className="w-3 h-3" /> Share
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 rounded-full text-destructive hover:text-destructive"
            onClick={() => void bulkArchive()}
          >
            <Trash2 className="w-3 h-3" /> Archive
          </Button>
          <button onClick={clearSelection} className="text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ─ Note dialog ──────────────────────────────────────────── */}
      <Dialog open={!!noteDialogFileId} onOpenChange={(open) => !open && setNoteDialogFileId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Add note</DialogTitle>
          </DialogHeader>
          <Textarea
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            placeholder="Write a note..."
            rows={4}
            className="text-sm"
          />
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setNoteDialogFileId(null)}>Cancel</Button>
            <Button size="sm" onClick={() => void submitNote()} disabled={noteSubmitting || !noteInput.trim()}>
              {noteSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─ Share dialog ─────────────────────────────────────────── */}
      <Dialog open={shareDialogOpen} onOpenChange={(open) => !open && closeShareDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Share2 className="w-4 h-4 text-primary" />
              Share {shareDialogFileIds.length > 1 ? `${shareDialogFileIds.length} files` : 'file'}
            </DialogTitle>
          </DialogHeader>

          {newShareUrls.length > 0 ? (
            <div className="space-y-3">
              {newShareUrls.map((entry) => (
                <div key={entry.url} className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  <p className="text-xs font-medium truncate">{entry.fileName}</p>

                  {/* One-time password display */}
                  {entry.plainPassword && (
                    <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 space-y-1">
                      <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> One-time password — save this now!
                      </p>
                      <div className="flex items-center gap-1.5">
                        <code className="flex-1 font-mono text-sm font-bold tracking-widest select-all">{entry.plainPassword}</code>
                        <button
                          onClick={() => void copyUrl(entry.plainPassword!)}
                          className="flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted transition-colors"
                        >
                          {copied === entry.plainPassword ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                      <p className="text-[10px] text-amber-600 dark:text-amber-500">This password will NOT be shown again.</p>
                    </div>
                  )}

                  {/* Share URL row */}
                  <div className="flex items-center gap-1.5">
                    <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-[11px] font-mono">{entry.url}</code>
                    <button
                      onClick={() => void copyUrl(entry.url)}
                      className="flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted transition-colors"
                    >
                      {copied === entry.url ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                      {copied === entry.url ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      title="Share via WhatsApp"
                      onClick={() => shareViaWhatsApp(entry.url, entry.fileName, !!entry.plainPassword)}
                      className="flex items-center rounded border px-2 py-1 text-xs hover:bg-green-50 hover:border-green-400 hover:text-green-700 transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                    <a href={entry.url} target="_blank" rel="noreferrer"
                      className="flex items-center rounded border px-2 py-1 text-xs hover:bg-muted transition-colors">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  {/* Email send button */}
                  {entry.recipientEmail && (
                    <button
                      disabled={emailSending === entry.shareId || emailSent.has(entry.shareId)}
                      onClick={() => void sendShareEmail(entry.shareId, entry.recipientEmail!, entry.plainPassword)}
                      className="flex items-center gap-1.5 text-xs rounded border px-2.5 py-1.5 w-full justify-center
                        hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {emailSending === entry.shareId
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : emailSent.has(entry.shareId)
                          ? <Check className="w-3 h-3 text-green-600" />
                          : <Mail className="w-3.5 h-3.5" />}
                      {emailSent.has(entry.shareId)
                        ? `Email sent to ${entry.recipientEmail}`
                        : `Send to ${entry.recipientEmail}`}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Recipient name</label>
                  <Input
                    value={shareForm.recipientName}
                    onChange={(e) => setShareForm((f) => ({ ...f, recipientName: e.target.value }))}
                    placeholder="Optional"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Recipient email</label>
                  <Input
                    value={shareForm.recipientEmail}
                    onChange={(e) => setShareForm((f) => ({ ...f, recipientEmail: e.target.value }))}
                    placeholder="Optional"
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Note to recipient</label>
                <Input
                  value={shareForm.note}
                  onChange={(e) => setShareForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Optional message..."
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Link expires</label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1 w-full justify-between">
                      {shareForm.expiresInDays ? `${shareForm.expiresInDays} days` : 'Never'}
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {([['', 'Never'], ['7', '7 days'], ['30', '30 days'], ['90', '90 days']] as [string, string][]).map(([v, labelText]) => (
                      <DropdownMenuItem key={v} onClick={() => setShareForm((f) => ({ ...f, expiresInDays: v }))}>
                        {labelText}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}

          {shareDialogFileIds.length === 1 && shareLinks.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Share history</p>
              <div className="max-h-40 overflow-y-auto space-y-1.5">
                {shareLinks.map((link) => (
                  <div key={link.id} className={`flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs
                    ${link.is_active ? 'bg-background' : 'opacity-40 bg-muted/20'}`}>
                    <Link2 className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">
                      {link.recipient_name ?? link.recipient_email ?? 'Anonymous'}
                      {' · '}
                      <span className="text-muted-foreground">
                        {link.access_count} view{link.access_count !== 1 ? 's' : ''}
                      </span>
                    </span>
                    {link.is_active && (
                      <>
                        <button onClick={() => void copyUrl(link.shareUrl)}
                          className="text-muted-foreground hover:text-foreground">
                          {copied === link.shareUrl ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                        </button>
                        <button onClick={() => void revokeShareLink(link.id)}
                          className="text-muted-foreground hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={closeShareDialog}>Close</Button>
            {newShareUrls.length === 0 && (
              <Button size="sm" onClick={() => void createShareLinks()} disabled={shareCreating} className="gap-1.5">
                {shareCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                Create &amp; copy link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FilePurposeDialog
        open={pendingUploadFiles.length > 0}
        files={pendingUploadFiles}
        loading={uploading}
        defaultPurpose={intakeContext?.defaultPurpose}
        defaultActionMode={intakeContext?.defaultActionMode}
        title="What should we do with these uploaded files?"
        onCancel={() => setPendingUploadFiles([])}
        onConfirm={(value) => void handleUpload(pendingUploadFiles, value)}
      />
    </div>
  );
}

// ─── FileRow sub-component ────────────────────────────────────────

interface FileRowProps {
  file: FileRecord;
  readOnly: boolean;
  isSelected: boolean;
  onSelectToggle: () => void;
  allTags: FileTag[];
  tagInput: string;
  tagDialogOpen: boolean;
  onOpenTagDialog: () => void;
  onCloseTagDialog: () => void;
  onTagInputChange: (v: string) => void;
  onAddTag: (name: string) => void;
  onRemoveTag: (tagId: string) => void;
  onToggleBookmark: () => void;
  onOpenNoteDialog: () => void;
  onArchive: () => void;
  onShare: () => void;
}

function humanizePurpose(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase());
}

function humanizeEntityType(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase());
}

function routeForEntityLink(entityType: string, entityId: string): string | null {
  if (entityType === 'operational_signal') return `/e/operational-signals/${entityId}`;
  if (entityType === 'oi_incident') return `/e/oi-incidents/${entityId}`;
  if (entityType === 'oi_warranty_case') return `/e/oi-warranty-cases/${entityId}`;
  if (entityType === 'oi_inventory_signal') return `/e/oi-inventory-signals/${entityId}`;
  if (entityType === 'oi_ehs_event') return `/e/oi-ehs-events/${entityId}`;
  if (entityType === 'oi_fleet_health_snapshot') return `/e/oi-fleet-health-snapshots/${entityId}`;
  return null;
}

function FileRow({
  file,
  readOnly,
  isSelected,
  onSelectToggle,
  allTags,
  tagInput,
  tagDialogOpen,
  onOpenTagDialog,
  onCloseTagDialog,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
  onToggleBookmark,
  onOpenNoteDialog,
  onArchive,
  onShare,
}: FileRowProps) {
  const displayName = file.display_name ?? file.file_name ?? 'Unnamed file';
  const docType = file.doc_type ?? file.ai_doc_type ?? null;
  const metadata = file.metadata ?? {};
  const parseStatus = String(file.parse_status ?? 'pending');
  const ragStatus = String(file.rag_status ?? 'pending');
  const intakePurpose = typeof metadata['intakePurpose'] === 'string'
    ? metadata['intakePurpose']
    : typeof metadata['uploadPurpose'] === 'string'
      ? metadata['uploadPurpose']
      : null;
  const suggestedRoute = typeof metadata['suggestedRoute'] === 'string' ? metadata['suggestedRoute'] : null;
  const suggestedFields = metadata['suggestedFields'] && typeof metadata['suggestedFields'] === 'object'
    ? metadata['suggestedFields'] as Record<string, unknown>
    : null;
  const createdDraftNo = typeof metadata['createdDraftNo'] === 'string' ? metadata['createdDraftNo'] : null;
  const createdDraftRoute = typeof metadata['createdDraftRoute'] === 'string' ? metadata['createdDraftRoute'] : null;
  const linkedRecords = (file.entityLinks ?? []).filter((link) => link.entity_type !== 'workspace');

  const filteredTagSuggestions = allTags.filter(
    (t) => t.name.toLowerCase().includes(tagInput.toLowerCase()) &&
      !(file.tags ?? []).some((ft) => ft.id === t.id),
  );

  return (
    <div className={`flex items-start gap-3 px-3 py-3 hover:bg-muted/30 group transition-colors
      ${isSelected ? 'bg-primary/5' : ''}`}>
      {/* Checkbox */}
      <button
        onClick={onSelectToggle}
        className={`mt-1 shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors
          ${isSelected ? 'bg-primary border-primary' : 'border-border opacity-0 group-hover:opacity-100'}`}
      >
        {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
      </button>

      {/* Icon */}
      <div className="shrink-0 text-muted-foreground mt-0.5">
        <FileIcon mime={file.mime_type} className="w-7 h-7" />
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={file.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:underline truncate max-w-xs"
            title={displayName}
          >
            {displayName}
          </a>
          {docType && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 py-0">{docType}</Badge>
          )}
        </div>

        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
          <span>{formatBytes(file.file_size)}</span>
          <span>{formatDate(file.created_at)}</span>
          {file.source_channel && <span className="capitalize">{file.source_channel.replace(/_/g, ' ')}</span>}
        </div>

        <div className="mt-1 flex flex-wrap gap-1.5">
          <Badge variant="outline" className="text-[10px]">
            Parse: {parseStatus.replace(/_/g, ' ')}
          </Badge>
          {intakePurpose && (
            <Badge variant="outline" className="text-[10px]">
              Purpose: {humanizePurpose(intakePurpose)}
            </Badge>
          )}
          {suggestedRoute && (
            <Badge variant="outline" className="text-[10px]">
              Route: {suggestedRoute}
            </Badge>
          )}
          {createdDraftNo && (
            <Badge variant="secondary" className="text-[10px]">
              Draft: {createdDraftNo}
            </Badge>
          )}
          <Badge variant="outline" className="text-[10px]">
            Links: {linkedRecords.length}
          </Badge>
          {metadata['autoIndexKnowledge'] ? (
            <Badge variant="outline" className="text-[10px]">
              Knowledge: {ragStatus.replace(/_/g, ' ')}
            </Badge>
          ) : null}
        </div>

        {/* AI summary */}
        {file.ai_summary && (
          <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{file.ai_summary}</p>
        )}

        {linkedRecords.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {linkedRecords.slice(0, 4).map((link) => (
              routeForEntityLink(link.entity_type, link.entity_id) ? (
                <Badge key={link.id} variant="secondary" className="text-[10px]">
                  <Link to={routeForEntityLink(link.entity_type, link.entity_id)!} className="hover:underline">
                    {humanizeEntityType(link.entity_type)}
                    {link.entity_ref ? ` • ${link.entity_ref}` : ''}
                  </Link>
                </Badge>
              ) : (
                <Badge key={link.id} variant="secondary" className="text-[10px]">
                  {humanizeEntityType(link.entity_type)}
                  {link.entity_ref ? ` • ${link.entity_ref}` : ''}
                </Badge>
              )
            ))}
            {linkedRecords.length > 4 ? (
              <Badge variant="secondary" className="text-[10px]">
                +{linkedRecords.length - 4} more
              </Badge>
            ) : null}
          </div>
        )}

        {suggestedFields ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {Object.entries(suggestedFields)
              .filter(([, value]) => value !== null && value !== '')
              .slice(0, 4)
              .map(([key, value]) => (
                <Badge key={key} variant="outline" className="text-[10px]">
                  {key}: {String(value)}
                </Badge>
              ))}
          </div>
        ) : null}

        {createdDraftRoute ? (
          <div className="mt-1">
            <Link to={createdDraftRoute} className="text-[11px] text-primary hover:underline">
              Open created draft
            </Link>
          </div>
        ) : null}

        {/* Tags */}
        {(file.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {(file.tags ?? []).map((tag) => (
              <span key={tag.id} className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${tagColor(tag.color)}`}>
                {tag.name}
                {!readOnly && (
                  <button
                    onClick={() => onRemoveTag(tag.id)}
                    className="hover:opacity-70 ml-0.5"
                    title="Remove tag"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Tag input panel (inline) */}
        {!readOnly && tagDialogOpen && (
          <div className="mt-2 flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                autoFocus
                className="h-7 text-xs w-44"
                placeholder="Tag name..."
                value={tagInput}
                onChange={(e) => onTagInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) { onAddTag(tagInput); }
                  if (e.key === 'Escape') onCloseTagDialog();
                }}
              />
              <Button size="sm" className="h-7 text-xs px-2" onClick={() => tagInput.trim() && onAddTag(tagInput)}>Add</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={onCloseTagDialog}>✕</Button>
            </div>
            {filteredTagSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {filteredTagSuggestions.slice(0, 8).map((t) => (
                  <button
                    key={t.id}
                    className={`text-[10px] px-1.5 py-0.5 rounded-full cursor-pointer hover:opacity-80 ${tagColor(t.color)}`}
                    onClick={() => onAddTag(t.name)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Bookmark */}
        <button
          title={file.isBookmarked ? 'Remove bookmark' : 'Bookmark'}
          onClick={onToggleBookmark}
          className={`p-1 rounded hover:bg-muted transition-colors ${file.isBookmarked ? 'text-amber-500' : 'text-muted-foreground'}`}
        >
          {file.isBookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
        </button>

        {/* Download */}
        <a
          href={file.file_url}
          download={file.file_name ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
          title="Download"
        >
          <Download className="w-3.5 h-3.5" />
        </a>

        {!readOnly && (
          <>
            {/* Tag */}
            <button
              title="Add tag"
              onClick={onOpenTagDialog}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
            >
              <Tag className="w-3.5 h-3.5" />
            </button>

            {/* Note */}
            <button
              title="Add note"
              onClick={onOpenNoteDialog}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </button>

            {/* Share */}
            <button
              title="Share (create traceable link)"
              onClick={onShare}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>

            {/* Archive */}
            <button
              title="Archive"
              onClick={onArchive}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
